// Workspace room. A01 retired the pre-pivot project+channel split: the
// workspace IS the chat venue, and agents/messages/repo-connections all
// live directly under it. Active reads for message / agent_presence /
// pending_turn are on the SpacetimeDB SDK; the initial shell (workspace,
// agents, events) loads via HTTP so auth stays server-enforced.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TabBar, useActiveTab } from "./tabs/TabBar";
import { SandboxTab } from "./tabs/SandboxTab";
import { OperatorTab } from "./tabs/OperatorTab";
import { FilesTab } from "./FilesTab";
import { RoadTab } from "./tabs/RoadTab";
import { TelemetryTab } from "./tabs/TelemetryTab";
import { PersonasTab } from "./tabs/PersonasTab";
import { RecipesTab } from "./tabs/RecipesTab";
import { ReceiptsTab } from "./tabs/ReceiptsTab";
import { NewsfeedTab } from "./tabs/NewsfeedTab";
import { ModelsTab } from "./tabs/ModelsTab";
import { UsageTab } from "./tabs/UsageTab";
import { SettingsTab } from "./tabs/SettingsTab";
import { StatusTab } from "./tabs/StatusTab";
import { Drawer, DrawerHandle } from "./tabs/Drawer";
import { SimpleChat } from "./tabs/SimpleChat";
import { ThreadDrawerView } from "./ThreadDrawerView";
import { ThreadsPanel } from "./ThreadsPanel";
import { triggerBalanceRefresh } from "../chrome/OpenRouterBalance";
import { SEAT_META, type DrawerContent } from "./tabs/drawerContent";

declare const __APP_VERSION__: string;
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import { useTable } from "spacetimedb/react";
import { ArrowDown } from "lucide-react";
import { tables } from "../../spacetimedb-bindings";
import {
  ApiError,
  councilChat,
  createWorkspace,
  deleteMessage,
  deleteWorkspace,
  extractRows,
  getEntitlements,
  getWorkspace,
  getUser,
  listOrgWorkspaces,
  listOrgs,
  listWorkspaceAgents,
  modelChat,
  postMessage,
  renameWorkspace,
  type ChatMessage,
  type Entitlements,
} from "../api/client";
import {
  formatModelAge,
  formatModelModality,
  formatModelPrice,
  useOpenRouterModels,
} from "../api/openrouter";
import type {
  Agent,
  AgentStatus,
  AttachmentRow,
  Message,
  MentionOption,
  ModelSort,
  RepoConnection,
  Workspace,
} from "./types";
import {
  bigintToNumber,
  mentionOptions,
  normaliseAgentStatus,
  rowToAgent,
  rowToWorkspace,
} from "./rowParsers";
import { AttachmentRenderer } from "./AttachmentRenderer";
import { CostBadge } from "./CostBadge";
import { ErrorToast } from "./ErrorToast";
import { Composer } from "./Composer";
import { WorkspaceUpgradeGate } from "./WorkspaceUpgradeGate";
import { RailTree } from "./RailTree";
import { MessageCard } from "./MessageCard";
// ActiveWatchersStrip removed from the workspace chrome 2026-04-27;
// watcher state has room in the Operations tab now.
import { usePresenceHeartbeat } from "./usePresenceHeartbeat";

export function WorkspaceRoomPage() {
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const activeTab = useActiveTab();
  const navigate = useNavigate();
  const user = getUser();
  const workspaceIdNum = workspaceId ? Number(workspaceId) : 0;

  // GitHub OAuth popup-callback bridge. The /github/oauth/callback
  // route on the backend redirects the BROWSER to /workspaces/:id?
  // github=connected. When that landing happens inside a popup
  // window, post a message back to the opener and close — opener
  // refreshes its state without a full reload. When it lands in
  // the original tab (popup blocked + same-tab fallback path), we
  // just clean the URL and let downstream queries re-fetch.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.get("github") !== "connected") return;
    if (workspaceIdNum <= 0) return;
    if (window.opener && !window.opener.closed) {
      try {
        window.opener.postMessage(
          { type: "wt-gh-connected", workspaceId: workspaceIdNum },
          window.location.origin,
        );
      } catch {
        /* opener cross-origin or gone — fall through */
      }
      window.close();
      return;
    }
    // Same-tab landing: strip the query so a refresh doesn't re-fire.
    params.delete("github");
    const next = params.toString();
    const url = next ? `${window.location.pathname}?${next}` : window.location.pathname;
    window.history.replaceState({}, "", url);
  }, [workspaceIdNum]);

  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [agents, setAgents] = useState<Agent[]>([]);

  const [userWorkspaces, setUserWorkspaces] = useState<
    Array<{ workspace_id: number; org_id: number; slug: string; name: string }>
  >([]);
  const [orgs, setOrgs] = useState<Array<{ org_id: number; name: string }>>([]);
  const [newWorkspaceOpen, setNewWorkspaceOpen] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState("");
  const [newWorkspaceBusy, setNewWorkspaceBusy] = useState(false);
  const [armedDeleteWorkspaceId, setArmedDeleteWorkspaceId] = useState<
    number | null
  >(null);
  const armWorkspaceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [renamingWorkspaceId, setRenamingWorkspaceId] = useState<number | null>(
    null,
  );
  const [renameDraft, setRenameDraft] = useState("");
  const [armedDeleteMessageId, setArmedDeleteMessageId] = useState<number | null>(
    null,
  );
  const armMessageTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [newMessage, setNewMessage] = useState("");
  const [mentionCaret, setMentionCaret] = useState<number | null>(null);
  const [mentionActiveIdx, setMentionActiveIdx] = useState(0);
  const messageInputRef = useRef<HTMLTextAreaElement | null>(null);
  const mentionListRef = useRef<HTMLUListElement | null>(null);
  const mentionPopupRef = useRef<HTMLDivElement | null>(null);

  const COLUMN_WIDTH_PX = 160;

  useEffect(() => {
    document.documentElement.style.setProperty(
      "--wt-content-left",
      `${COLUMN_WIDTH_PX}px`,
    );
    document.documentElement.style.setProperty("--wt-content-top", "56px");
    return () => {
      document.documentElement.style.removeProperty("--wt-content-left");
      document.documentElement.style.removeProperty("--wt-content-top");
    };
  }, [COLUMN_WIDTH_PX]);

  const messagesScrollRef = useRef<HTMLDivElement | null>(null);
  const [composerHeight, setComposerHeight] = useState(0);
  const composerObserverRef = useRef<ResizeObserver | null>(null);
  const composerRef = useCallback((node: HTMLFormElement | null) => {
    composerObserverRef.current?.disconnect();
    if (!node) {
      setComposerHeight(0);
      return;
    }
    setComposerHeight(node.getBoundingClientRect().height);
    const ro = new ResizeObserver(() => {
      setComposerHeight(node.getBoundingClientRect().height);
    });
    ro.observe(node);
    composerObserverRef.current = ro;
  }, []);
  const atBottomRef = useRef(true);

  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  function handleMessagesScroll() {
    const el = messagesScrollRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = distance < 120;
    atBottomRef.current = atBottom;
    setShowScrollToBottom(!atBottom);
  }

  function scrollToBottom() {
    const el = messagesScrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
    atBottomRef.current = true;
    setShowScrollToBottom(false);
  }

  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [directReplyInFlight] = useState<Set<string>>(() => new Set());

  // A34 — drawer IS the canonical team chat. Default open + default
  // to team_chat. Tab clicks no longer reset the drawer to a seat
  // chat; opening a seat / model / thread is an explicit user action
  // (e.g. clicking a model in the Models tab calls openDrawerContent).
  const [drawerOpen, setDrawerOpen] = useState(true);
  const [drawerContent, setDrawerContent] = useState<DrawerContent>({
    kind: "team_chat",
  });
  const openDrawerContent = useCallback((content: DrawerContent) => {
    setDrawerContent(content);
    setDrawerOpen(true);
  }, []);

  const [modelSort, setModelSort] = useState<ModelSort>(() => {
    try {
      const v = localStorage.getItem("wt_model_sort");
      if (
        v === "relevance" ||
        v === "age-new" ||
        v === "age-old" ||
        v === "price-low" ||
        v === "price-high"
      ) {
        return v;
      }
    } catch {
      // fall through
    }
    return "relevance";
  });
  useEffect(() => {
    try {
      localStorage.setItem("wt_model_sort", modelSort);
    } catch {
      // ignore
    }
  }, [modelSort]);
  const { models: openRouterModels } = useOpenRouterModels();

  const workspaceIdBig = BigInt(workspaceIdNum);

  // A30 — entitlement-driven cap-hit gate. Poll every 3s; refresh on
  // tab focus. The chat composer is replaced by WorkspaceUpgradeGate
  // when the workspace is paused (cap hit on free tier).
  const [entitlements, setEntitlements] = useState<Entitlements | null>(null);
  useEffect(() => {
    if (workspaceIdNum <= 0) return;
    let cancelled = false;
    const refresh = () => {
      void getEntitlements(workspaceIdNum)
        .then((e) => {
          if (!cancelled) setEntitlements(e);
        })
        .catch(() => {});
    };
    refresh();
    const interval = window.setInterval(refresh, 3_000);
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
      window.removeEventListener("focus", onFocus);
    };
  }, [workspaceIdNum]);
  const workspacePaused = entitlements?.workspace_paused === true;

  // A38 — presence heartbeat. Reports activity so the human-reach
  // watcher can decide whether to email an unanswered question.
  usePresenceHeartbeat(workspaceIdNum);

  const [messageRows] = useTable(
    tables.message.where((r) => r.workspaceId.eq(workspaceIdBig)),
  );
  const [presenceRows] = useTable(
    tables.agent_presence.where((r) => r.workspaceId.eq(workspaceIdBig)),
  );
  const [pendingTurnRows] = useTable(
    tables.pending_turn.where((r) => r.workspaceId.eq(workspaceIdBig)),
  );
  const [attachmentRows] = useTable(tables.message_attachment);
  const [pendingCommitRows] = useTable(
    tables.pending_commit.where((r) => r.workspaceId.eq(workspaceIdBig)),
  );
  const [routeRows] = useTable(
    tables.pending_route.where((r) => r.workspaceId.eq(workspaceIdBig)),
  );
  const [chunkRows] = useTable(tables.message_chunk);
  const [statusRows] = useTable(tables.message_status);
  const [repoConnectionRows] = useTable(
    tables.repo_connection.where((r) => r.workspaceId.eq(workspaceIdBig)),
  );

  const messages = useMemo<Message[]>(() => {
    return messageRows
      .map((row) => ({
        message_id: bigintToNumber(row.messageId),
        workspace_id: bigintToNumber(row.workspaceId),
        author_kind: row.authorKind,
        author_agent_id:
          row.authorAgentId === null || row.authorAgentId === undefined
            ? null
            : bigintToNumber(row.authorAgentId),
        body: row.body,
        created_at: bigintToNumber(row.createdAt),
      }))
      // A34 — drawer-as-canonical-team-chat. `agent_stdout` is routine
      // working-agent CLI output that the user watches in the Sandbox
      // tab via the noVNC display. Filtering it here keeps the drawer
      // signal-rich (team commentary only).
      .filter((m) => m.author_kind !== "event" && m.author_kind !== "agent_stdout")
      .sort((a, b) => a.created_at - b.created_at);
  }, [messageRows]);

  const presenceByAgentId = useMemo(() => {
    const map = new Map<number, AgentStatus>();
    for (const row of presenceRows) {
      map.set(bigintToNumber(row.agentId), normaliseAgentStatus(row.status));
    }
    return map;
  }, [presenceRows]);

  const pendingCommitIdByMessageId = useMemo(() => {
    const map = new Map<number, number>();
    for (const row of pendingCommitRows) {
      if (row.status !== "awaiting_approval") continue;
      map.set(bigintToNumber(row.messageId), bigintToNumber(row.pendingCommitId));
    }
    return map;
  }, [pendingCommitRows]);

  const attachmentsByMessageId = useMemo(() => {
    const map = new Map<number, AttachmentRow[]>();
    for (const row of attachmentRows) {
      const normalised: AttachmentRow = {
        attachment_id: bigintToNumber(row.attachmentId),
        message_id: bigintToNumber(row.messageId),
        kind: row.kind,
        payload_json: row.payloadJson,
        created_at: bigintToNumber(row.createdAt),
      };
      const existing = map.get(normalised.message_id) ?? [];
      existing.push(normalised);
      map.set(normalised.message_id, existing);
    }
    for (const list of map.values()) {
      list.sort((a, b) => a.attachment_id - b.attachment_id);
    }
    return map;
  }, [attachmentRows]);

  const streamingBodyByMessageId = useMemo(() => {
    const grouped = new Map<number, Array<{ sequence: number; text: string }>>();
    for (const row of chunkRows) {
      const message_id = bigintToNumber(row.messageId);
      const list = grouped.get(message_id) ?? [];
      list.push({ sequence: row.sequence, text: row.text });
      grouped.set(message_id, list);
    }
    const result = new Map<number, string>();
    for (const [message_id, list] of grouped.entries()) {
      list.sort((a, b) => a.sequence - b.sequence);
      result.set(message_id, list.map((c) => c.text).join(""));
    }
    return result;
  }, [chunkRows]);

  const completedStreamingMessageIds = useMemo(() => {
    const ids = new Set<number>();
    for (const row of statusRows) {
      if (row.streamingComplete) {
        ids.add(bigintToNumber(row.messageId));
      }
    }
    return ids;
  }, [statusRows]);

  const repoConnections = useMemo<RepoConnection[]>(() => {
    return repoConnectionRows
      .map((row) => ({
        repo_connection_id: bigintToNumber(row.repoConnectionId),
        workspace_id: bigintToNumber(row.workspaceId),
        github_owner: row.githubOwner,
        github_repo: row.githubRepo,
        default_branch: row.defaultBranch,
        connected_at: bigintToNumber(row.connectedAt),
      }))
      .sort((a, b) =>
        `${a.github_owner}/${a.github_repo}`.localeCompare(
          `${b.github_owner}/${b.github_repo}`,
        ),
      );
  }, [repoConnectionRows]);

  const priorCompletedCountRef = useRef(0);
  useEffect(() => {
    const count = completedStreamingMessageIds.size;
    if (count > priorCompletedCountRef.current) {
      priorCompletedCountRef.current = count;
      triggerBalanceRefresh();
      const id = window.setTimeout(() => triggerBalanceRefresh(), 5000);
      return () => window.clearTimeout(id);
    }
  }, [completedStreamingMessageIds]);

  const INFLIGHT_TIMEOUT_MS = 120_000;
  const [nowTick, setNowTick] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNowTick(Date.now()), 500);
    return () => window.clearInterval(id);
  }, []);

  const activeRoutingCount = useMemo(() => {
    if (!workspaceIdNum) return 0;
    const cutoffMicros = (nowTick - 1_000) * 1000;
    let n = 0;
    for (const row of routeRows) {
      if (row.status !== "pending" && row.status !== "locked") continue;
      if (bigintToNumber(row.workspaceId) !== workspaceIdNum) continue;
      if (bigintToNumber(row.createdAt) < cutoffMicros) continue;
      n += 1;
    }
    return n;
  }, [routeRows, workspaceIdNum, nowTick]);

  const activeInflight = useMemo(() => {
    if (!workspaceIdNum)
      return [] as Array<{ agentId: number; status: string }>;

    const latestReplyAtByAgent = new Map<number, number>();
    for (const msg of messages) {
      if (msg.author_kind !== "agent" || msg.author_agent_id == null) continue;
      const prev = latestReplyAtByAgent.get(msg.author_agent_id) ?? 0;
      if (msg.created_at > prev) {
        latestReplyAtByAgent.set(msg.author_agent_id, msg.created_at);
      }
    }

    const out: Array<{ agentId: number; status: string }> = [];
    const seen = new Set<number>();
    const cutoffMicros = (nowTick - INFLIGHT_TIMEOUT_MS) * 1000;
    for (const row of pendingTurnRows) {
      if (row.status !== "pending" && row.status !== "locked") continue;
      if (bigintToNumber(row.workspaceId) !== workspaceIdNum) continue;
      const createdAt = bigintToNumber(row.createdAt);
      if (createdAt < cutoffMicros) continue;
      const agentId = bigintToNumber(row.targetAgentId);
      const latestReply = latestReplyAtByAgent.get(agentId) ?? 0;
      if (latestReply > createdAt) continue;
      if (seen.has(agentId)) continue;
      seen.add(agentId);
      out.push({
        agentId,
        status: presenceByAgentId.get(agentId) ?? "thinking",
      });
    }
    return out;
  }, [pendingTurnRows, presenceByAgentId, workspaceIdNum, nowTick, messages]);

  const workspaceAgentIds = useMemo(() => {
    const ids = new Set<number>();
    for (const msg of messages) {
      if (msg.author_kind === "agent" && msg.author_agent_id !== null) {
        ids.add(msg.author_agent_id);
      }
    }
    for (const row of pendingTurnRows) {
      if (bigintToNumber(row.workspaceId) === workspaceIdNum) {
        ids.add(bigintToNumber(row.targetAgentId));
      }
    }
    return ids;
  }, [messages, pendingTurnRows, workspaceIdNum]);

  const workspaceLoadGenRef = useRef(0);

  async function refreshWorkspaceShell() {
    if (!workspaceIdNum) return;
    const targetId = workspaceIdNum;
    const gen = ++workspaceLoadGenRef.current;
    const [wsRes, agRes] = await Promise.all([
      getWorkspace(targetId),
      listWorkspaceAgents(targetId),
    ]);
    if (gen !== workspaceLoadGenRef.current) return;
    const ws =
      extractRows(wsRes).map(rowToWorkspace).find((p) => p !== null) ?? null;
    const ags = extractRows(agRes)
      .map(rowToAgent)
      .filter((a): a is Agent => a !== null);
    setWorkspace(ws);
    setAgents(ags);
  }

  const refreshUserWorkspaces = useCallback(async () => {
    try {
      const orgsRes = await listOrgs();
      const orgRows = extractRows(orgsRes);
      const orgIds: number[] = [];
      const orgList: Array<{ org_id: number; name: string }> = [];
      for (const row of orgRows) {
        const id = typeof row[0] === "number" ? row[0] : Number(row[0]);
        const name = typeof row[2] === "string" ? row[2] : "";
        if (Number.isFinite(id) && id > 0) {
          orgIds.push(id);
          if (name) orgList.push({ org_id: id, name });
        }
      }
      setOrgs(orgList);
      const perOrg = await Promise.all(
        orgIds.map(async (orgId) => {
          try {
            const res = await listOrgWorkspaces(orgId);
            return extractRows(res)
              .map((row) => {
                const wid =
                  typeof row[0] === "number" ? row[0] : Number(row[0]);
                const oid =
                  typeof row[1] === "number" ? row[1] : Number(row[1]);
                const slug = typeof row[2] === "string" ? row[2] : "";
                const name = typeof row[3] === "string" ? row[3] : "";
                if (!wid || !oid || !slug || !name) return null;
                return { workspace_id: wid, org_id: oid, slug, name };
              })
              .filter(
                (
                  p,
                ): p is {
                  workspace_id: number;
                  org_id: number;
                  slug: string;
                  name: string;
                } => p !== null,
              );
          } catch {
            return [];
          }
        }),
      );
      const flat = perOrg.flat().sort((a, b) => a.name.localeCompare(b.name));
      setUserWorkspaces(flat);
    } catch {
      // Rail tree is supplementary — single-workspace view still works.
    }
  }, []);

  useEffect(() => {
    void refreshUserWorkspaces();
  }, [refreshUserWorkspaces]);

  useEffect(() => {
    if (!workspaceIdNum) return;
    let cancelled = false;
    async function load() {
      try {
        await refreshWorkspaceShell();
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof ApiError ? err.message : "Could not load workspace",
          );
        }
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceIdNum]);

  async function submitMessage(e: React.FormEvent) {
    e.preventDefault();
    if (!workspaceIdNum || newMessage.trim() === "") return;
    const body = newMessage.trim();
    setError("");
    setBusy(true);
    try {
      await postMessage(workspaceIdNum, body);
      setNewMessage("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not post message");
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteMessage(messageId: number) {
    if (armedDeleteMessageId === messageId) {
      if (armMessageTimerRef.current) clearTimeout(armMessageTimerRef.current);
      setArmedDeleteMessageId(null);
      try {
        await deleteMessage(messageId);
      } catch (err) {
        setError(
          err instanceof ApiError ? err.message : "Could not delete message",
        );
      }
      return;
    }
    setArmedDeleteMessageId(messageId);
    if (armMessageTimerRef.current) clearTimeout(armMessageTimerRef.current);
    armMessageTimerRef.current = setTimeout(
      () => setArmedDeleteMessageId(null),
      3000,
    );
  }

  async function submitCreateWorkspace(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = newWorkspaceName.trim();
    if (trimmed === "") {
      setError("Workspace name is required.");
      return;
    }
    const orgId = workspace?.org_id ?? userWorkspaces[0]?.org_id ?? 0;
    if (!orgId) {
      setError("No org to create this workspace under.");
      return;
    }
    const slug = trimmed
      .toLowerCase()
      .replace(/[^a-z0-9-_]/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "")
      .slice(0, 48);
    if (slug === "") {
      setError("Workspace name must contain at least one letter or digit.");
      return;
    }
    setNewWorkspaceBusy(true);
    setError("");
    try {
      await createWorkspace(orgId, slug, trimmed);
      setNewWorkspaceName("");
      setNewWorkspaceOpen(false);
      await refreshUserWorkspaces();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not create workspace",
      );
    } finally {
      setNewWorkspaceBusy(false);
    }
  }

  function startRenameWorkspace(w: { workspace_id: number; name: string }) {
    setRenamingWorkspaceId(w.workspace_id);
    setRenameDraft(w.name);
    setError("");
  }

  function cancelRenameWorkspace() {
    setRenamingWorkspaceId(null);
    setRenameDraft("");
  }

  async function submitRenameWorkspace(e: React.FormEvent) {
    e.preventDefault();
    if (renamingWorkspaceId == null) return;
    const target = renamingWorkspaceId;
    const name = renameDraft.trim();
    if (name === "") {
      cancelRenameWorkspace();
      return;
    }
    try {
      await renameWorkspace(target, name);
      setRenamingWorkspaceId(null);
      setRenameDraft("");
      await refreshUserWorkspaces();
      // If the active workspace was the one renamed, refresh its shell
      // so the header/chrome pick up the new name.
      if (target === workspaceIdNum) {
        await refreshWorkspaceShell();
      }
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Could not rename workspace",
      );
    }
  }

  async function handleDeleteWorkspace(w: {
    workspace_id: number;
    name: string;
  }) {
    if (w.workspace_id === workspaceIdNum) {
      setError("Switch to another workspace before deleting this one.");
      return;
    }
    if (armedDeleteWorkspaceId === w.workspace_id) {
      if (armWorkspaceTimerRef.current)
        clearTimeout(armWorkspaceTimerRef.current);
      setArmedDeleteWorkspaceId(null);
      try {
        await deleteWorkspace(w.workspace_id);
        await refreshUserWorkspaces();
      } catch (err) {
        setError(
          err instanceof ApiError ? err.message : "Could not delete workspace",
        );
      }
      return;
    }
    setArmedDeleteWorkspaceId(w.workspace_id);
    if (armWorkspaceTimerRef.current)
      clearTimeout(armWorkspaceTimerRef.current);
    armWorkspaceTimerRef.current = setTimeout(
      () => setArmedDeleteWorkspaceId(null),
      3000,
    );
  }

  const targetedAgents = mentionOptions(newMessage, agents);

  const mentionContext = useMemo(() => {
    if (mentionCaret === null) return null;
    const before = newMessage.slice(0, mentionCaret);
    const atIdx =
      Math.max(before.lastIndexOf(" "), before.lastIndexOf("\n")) + 1;
    const token = before.slice(atIdx);
    if (!token.startsWith("@")) return null;
    const query = token.slice(1).toLowerCase();
    return { atPosition: atIdx, query };
  }, [newMessage, mentionCaret]);

  const mentionMatches = useMemo<MentionOption[]>(() => {
    if (!mentionContext) return [];
    const q = mentionContext.query;
    const agentScored = agents
      .map((agent) => {
        const seat = agent.seat_key.toLowerCase();
        const name = agent.display_name.toLowerCase();
        if (q === "") return { agent, score: 0 };
        if (seat.startsWith(q)) return { agent, score: 0 };
        if (name.startsWith(q)) return { agent, score: 1 };
        if (seat.includes(q) || name.includes(q))
          return { agent, score: 2 };
        return null;
      })
      .filter((m): m is { agent: Agent; score: number } => m !== null)
      .sort(
        (a, b) =>
          a.score - b.score ||
          a.agent.seat_key.localeCompare(b.agent.seat_key),
      );

    const seenSeats = new Set(agents.map((a) => a.seat_key.toLowerCase()));

    const priceOf = (m: (typeof openRouterModels)[number]): number => {
      const p = parseFloat(m.pricing?.prompt ?? "0");
      const c = parseFloat(m.pricing?.completion ?? "0");
      if (!Number.isFinite(p) || !Number.isFinite(c)) return Number.MAX_VALUE;
      return p * 1000 + c * 300;
    };
    const createdOf = (m: (typeof openRouterModels)[number]): number =>
      m.created ?? 0;
    const modelScored = openRouterModels
      .map((model) => {
        const id = model.id.toLowerCase();
        const name = model.name.toLowerCase();
        if (seenSeats.has(id)) return null;
        if (q === "") return { model, score: 3 };
        if (id.startsWith(q)) return { model, score: 0 };
        if (name.startsWith(q)) return { model, score: 1 };
        if (id.includes(q) || name.includes(q))
          return { model, score: 2 };
        return null;
      })
      .filter(
        (
          m,
        ): m is { model: (typeof openRouterModels)[number]; score: number } =>
          m !== null,
      )
      .sort((a, b) => {
        if (q !== "" || modelSort === "relevance") {
          return (
            a.score - b.score || a.model.id.localeCompare(b.model.id)
          );
        }
        if (modelSort === "age-new") return createdOf(b.model) - createdOf(a.model);
        if (modelSort === "age-old") return createdOf(a.model) - createdOf(b.model);
        if (modelSort === "price-low") return priceOf(a.model) - priceOf(b.model);
        if (modelSort === "price-high") return priceOf(b.model) - priceOf(a.model);
        return a.model.id.localeCompare(b.model.id);
      });

    return [
      ...agentScored.map((s) => ({ kind: "agent" as const, agent: s.agent })),
      ...modelScored.map((s) => ({
        kind: "model" as const,
        modelId: s.model.id,
        modelName: s.model.name,
        price: formatModelPrice(s.model),
        age: formatModelAge(s.model),
        modality: formatModelModality(s.model),
      })),
    ];
  }, [mentionContext, agents, openRouterModels, modelSort]);

  useEffect(() => {
    if (mentionActiveIdx >= mentionMatches.length) {
      setMentionActiveIdx(0);
    }
  }, [mentionMatches, mentionActiveIdx]);

  useEffect(() => {
    const list = mentionListRef.current;
    if (!list) return;
    const active = list.querySelector<HTMLElement>("[data-active-row='true']");
    if (active) active.scrollIntoView({ block: "nearest" });
  }, [mentionActiveIdx, mentionMatches.length]);

  useEffect(() => {
    const el = messagesScrollRef.current;
    if (!el) return;
    if (atBottomRef.current) el.scrollTop = el.scrollHeight;
  }, [
    messages,
    streamingBodyByMessageId,
    completedStreamingMessageIds,
    workspaceIdNum,
  ]);

  useEffect(() => {
    const el = messagesScrollRef.current;
    if (!el) return;
    const target = el.firstElementChild;
    if (!target || !(target instanceof Element)) return;
    const observer = new ResizeObserver(() => {
      if (atBottomRef.current) el.scrollTop = el.scrollHeight;
    });
    observer.observe(target);
    observer.observe(el);
    return () => observer.disconnect();
  }, [workspaceIdNum]);

  useEffect(() => {
    const unknown = messages.some(
      (m) =>
        m.author_kind === "agent" &&
        m.author_agent_id !== null &&
        !agents.some((a) => a.agent_id === m.author_agent_id),
    );
    if (unknown) {
      void refreshWorkspaceShell();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  function applyMention(option: MentionOption) {
    if (!mentionContext) return;
    const before = newMessage.slice(0, mentionContext.atPosition);
    const after = newMessage.slice(mentionCaret ?? newMessage.length);
    const token =
      option.kind === "agent" ? option.agent.seat_key : option.modelId;
    const inserted = `@${token} `;
    const next = `${before}${inserted}${after}`;
    setNewMessage(next);
    const nextCaret = before.length + inserted.length;
    setMentionCaret(null);
    setMentionActiveIdx(0);
    window.setTimeout(() => {
      const input = messageInputRef.current;
      if (!input) return;
      input.focus();
      input.setSelectionRange(nextCaret, nextCaret);
    }, 0);
  }

  function handleComposerKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (mentionMatches.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setMentionActiveIdx((idx) => (idx + 1) % mentionMatches.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setMentionActiveIdx(
          (idx) =>
            (idx - 1 + mentionMatches.length) % mentionMatches.length,
        );
        return;
      }
      if (e.key === "Enter" || e.key === "Tab") {
        const target = mentionMatches[mentionActiveIdx];
        if (target) {
          e.preventDefault();
          applyMention(target);
          return;
        }
      }
      if (e.key === "Escape") {
        e.preventDefault();
        setMentionCaret(null);
        return;
      }
    }
    const isComposing = (e.nativeEvent as KeyboardEvent).isComposing;
    if (e.key === "Enter" && !e.shiftKey && !isComposing) {
      if (newMessage.trim() === "") return;
      e.preventDefault();
      void submitMessage(e as unknown as React.FormEvent);
    }
  }

  const envLabel = (() => {
    const apiUrl = (import.meta.env.VITE_API_URL as string | undefined) ?? "";
    if (apiUrl.includes("staging")) return "staging";
    if (apiUrl.includes("waytide.dev")) return "production";
    return "local";
  })();
  const appVersion =
    typeof __APP_VERSION__ === "undefined" ? "0.1.0" : __APP_VERSION__;

  function drawerTitle(): string {
    if (drawerContent.kind === "team_chat") {
      return "Team chat";
    }
    if (drawerContent.kind === "seat") {
      return SEAT_META[drawerContent.seat].displayName;
    }
    if (drawerContent.kind === "thread") {
      return drawerContent.title;
    }
    return drawerContent.modelName || drawerContent.modelId;
  }

  function renderDrawerBody() {
    const orgId = workspace?.org_id ?? null;
    if (orgId == null) {
      return (
        <div className="p-4 text-xs text-muted">
          No org resolved for this workspace yet — the drawer needs an org
          to route chat through.
        </div>
      );
    }
    // A34 — drawer IS the canonical team chat. The workspace-scoped
    // message log + composer render here. Watcher fires, Vela
    // coordination, the user, and the working agent (when @-mentioned)
    // all converse through this surface. Routine working-agent stdout
    // is filtered (`author_kind = "agent_stdout"`) so the user watches
    // it in the Sandbox tab via noVNC.
    if (drawerContent.kind === "team_chat") {
      return renderTeamChat();
    }
    if (drawerContent.kind === "seat") {
      const meta = SEAT_META[drawerContent.seat];
      const seatKey = drawerContent.seat;
      return (
        <SimpleChat
          key={`seat-${seatKey}`}
          testId={`drawer-chat-${seatKey}`}
          storageKey={`${orgId}:seat:${seatKey}`}
          assistantLabel={`${meta.displayName} — ${meta.role}`}
          greeting={meta.greeting}
          placeholder={`Ask ${meta.displayName}…`}
          onSend={async (msgs: ChatMessage[]) =>
            councilChat(seatKey, orgId, msgs)
          }
        />
      );
    }
    if (drawerContent.kind === "thread") {
      return (
        <ThreadDrawerView
          key={`thread-${drawerContent.threadId}`}
          threadId={drawerContent.threadId}
          title={drawerContent.title}
          threadKind={drawerContent.threadKind}
        />
      );
    }
    const model = drawerContent;
    const modelLabel = model.modelName
      ? `${model.modelName} — ${model.modelId}`
      : model.modelId;
    return (
      <SimpleChat
        key={`model-${model.modelId}`}
        testId={`drawer-chat-model-${model.modelId}`}
        storageKey={`${orgId}:model:${model.modelId}`}
        assistantLabel={modelLabel}
        greeting={
          model.description
            ? `${model.description}\n\nAsk anything — the transcript persists on this browser.`
            : "Ask anything — the transcript persists on this browser."
        }
        placeholder={`Talk to ${model.modelId}…`}
        onSend={async (msgs: ChatMessage[]) =>
          modelChat(orgId, model.modelId, msgs)
        }
      />
    );
  }

  // A34 — the canonical team-chat surface. Renders the workspace's
  // message log + composer inside the drawer. agent_stdout-kind
  // messages are filtered out (filter applied where messages are
  // mapped, see useMemo above) so the drawer stays signal-rich while
  // routine working-agent CLI output renders only in the Sandbox tab.
  function renderTeamChat() {
    return (
      <div className="relative h-full" data-testid="team-chat-panel">
        <div
          className="absolute left-0 right-0 top-0 z-10 overflow-y-auto border-b border-edge bg-surface/95 px-3 py-2 backdrop-blur"
          style={{ height: 168 }}
        >
          <div className="mb-2">
            <div className="mb-1 flex items-center justify-between gap-2">
              <p className="text-xs font-semibold text-primary">Team present</p>
              <button
                type="button"
                onClick={() => openDrawerContent({ kind: "team_chat" })}
                className="rounded-md border border-edge px-2 py-0.5 text-[0.625rem] text-muted hover:border-accent hover:text-accent"
              >
                # team
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="team-presence-strip">
              {agents.length === 0 ? (
                <span className="text-[0.625rem] text-faint">
                  Vela is getting the workspace ready.
                </span>
              ) : (
                agents.map((agent) => {
                  const status =
                    presenceByAgentId.get(agent.agent_id) ?? "offline";
                  const tone =
                    status === "thinking" || status === "replying"
                      ? "bg-accent"
                      : status === "idle"
                        ? "bg-success"
                        : "bg-faint";
                  return (
                    <span
                      key={agent.agent_id}
                      className="inline-flex min-w-0 max-w-full items-center gap-1 rounded-md border border-edge bg-surface-raised px-1.5 py-0.5 text-[0.625rem] text-muted"
                      title={`${agent.display_name} @${agent.seat_key} - ${status}`}
                    >
                      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${tone}`} />
                      <span className="truncate">@{agent.seat_key}</span>
                    </span>
                  );
                })
              )}
            </div>
          </div>
          <ThreadsPanel
            workspaceId={workspaceIdNum}
            onOpenDrawerContent={openDrawerContent}
            compact
          />
        </div>
        <div
          ref={messagesScrollRef}
          onScroll={handleMessagesScroll}
          className="absolute left-0 right-0 overflow-y-auto px-3 pt-3"
          style={{ top: 168, bottom: composerHeight }}
          data-testid="messages-list"
        >
          {messages.length === 0 && workspaceIdNum > 0 && (
            <p className="text-sm text-muted">
              No messages yet. Post the first one.
            </p>
          )}
          <ul className="flex flex-col">
            {messages.map((message, idx) => {
              const agent = agents.find(
                (a) => a.agent_id === message.author_agent_id,
              );
              const attachments =
                attachmentsByMessageId.get(message.message_id) ?? [];
              const isStreamingComplete = completedStreamingMessageIds.has(
                message.message_id,
              );
              const streamingBody = streamingBodyByMessageId.get(
                message.message_id,
              );
              const body =
                message.author_kind === "agent" &&
                !isStreamingComplete &&
                streamingBody !== undefined
                  ? streamingBody
                  : message.body;

              if (
                message.author_kind === "agent" &&
                body.trim() === "" &&
                attachments.length === 0
              ) {
                return null;
              }

              const prev = messages[idx - 1];
              const sameAuthor =
                prev !== undefined &&
                prev.author_kind === message.author_kind &&
                prev.author_agent_id === message.author_agent_id &&
                message.created_at - prev.created_at < 5 * 60 * 1_000_000;

              const authorName =
                message.author_kind === "agent"
                  ? (agent?.display_name ?? "Agent")
                  : message.author_kind === "user"
                    ? (user?.display_name ?? user?.email ?? "You")
                    : message.author_kind;
              const authorColor =
                message.author_kind === "agent"
                  ? "text-accent"
                  : message.author_kind === "user"
                    ? "text-primary"
                    : "text-warning";

              const timestamp = new Date(
                message.created_at / 1000,
              ).toLocaleTimeString(undefined, {
                hour: "2-digit",
                minute: "2-digit",
              });

              const messageArmed =
                armedDeleteMessageId === message.message_id;
              return (
                <MessageCard
                  key={message.message_id}
                  author={{
                    name: authorName,
                    colorClass: authorColor,
                    meta: agent ? `@${agent.seat_key}` : undefined,
                  }}
                  alignment={
                    message.author_kind === "user" ? "me" : "other"
                  }
                  sameAuthor={sameAuthor}
                  timestamp={timestamp}
                  onDelete={() =>
                    void handleDeleteMessage(message.message_id)
                  }
                  deleteArmed={messageArmed}
                  testId={`message-${message.author_kind}`}
                >
                  <div>
                    {body.trim() === "" ? null : (
                      <div className="prose prose-sm max-w-none wt-prose">
                        <ReactMarkdown
                          remarkPlugins={[remarkGfm]}
                          rehypePlugins={[rehypeHighlight]}
                        >
                          {body}
                        </ReactMarkdown>
                      </div>
                    )}
                    {(() => {
                      const costBadge = attachments.find(
                        (a) => a.kind === "run_summary",
                      );
                      const others = attachments.filter(
                        (a) => a.kind !== "run_summary",
                      );
                      return (
                        <>
                          {others.length > 0 && (
                            <div
                              className="mt-2 flex flex-col gap-2"
                              data-testid={`message-${message.message_id}-attachments`}
                            >
                              {others.map((attachment) => (
                                <AttachmentRenderer
                                  key={attachment.attachment_id}
                                  attachment={attachment}
                                  workspaceId={workspaceIdNum}
                                  pendingCommitIdByMessageId={
                                    pendingCommitIdByMessageId
                                  }
                                />
                              ))}
                            </div>
                          )}
                          {costBadge && (
                            <CostBadge
                              attachment={costBadge}
                              messageId={message.message_id}
                            />
                          )}
                        </>
                      );
                    })()}
                  </div>
                </MessageCard>
              );
            })}
          </ul>
        </div>
        {showScrollToBottom && (
          <button
            type="button"
            onClick={scrollToBottom}
            style={{ bottom: composerHeight + 12 }}
            className="glass-blur absolute right-3 z-30 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-edge bg-surface-raised/80 text-muted shadow-[var(--wt-shadow)] transition-colors hover:border-accent hover:text-primary"
            aria-label="Scroll to bottom"
            title="Scroll to bottom"
            data-testid="scroll-to-bottom"
          >
            <ArrowDown size={14} />
          </button>
        )}
        {workspaceIdNum > 0 && workspacePaused && (
          <WorkspaceUpgradeGate workspaceId={workspaceIdNum} />
        )}
        {workspaceIdNum > 0 && !workspacePaused && (
          <Composer
            workspaceId={workspaceIdNum}
            composerRef={composerRef}
            messageInputRef={messageInputRef}
            mentionPopupRef={mentionPopupRef}
            mentionListRef={mentionListRef}
            newMessage={newMessage}
            onNewMessageChange={setNewMessage}
            onMentionCaretChange={setMentionCaret}
            onKeyDown={handleComposerKeyDown}
            onSubmit={submitMessage}
            busy={busy}
            agents={agents}
            activeRoutingCount={activeRoutingCount}
            activeInflight={activeInflight}
            directReplyInFlight={directReplyInFlight}
            targetedAgents={targetedAgents}
            mentionMatches={mentionMatches}
            mentionActiveIdx={mentionActiveIdx}
            onMentionActiveIdxChange={setMentionActiveIdx}
            workspaceAgentCount={workspaceAgentIds.size}
            modelSort={modelSort}
            onModelSortChange={setModelSort}
            onMentionPick={applyMention}
          />
        )}
      </div>
    );
  }

  return (
    <div className="h-screen overflow-hidden text-primary transition-colors">
      <main
        className="grid h-screen"
        style={{
          gridTemplateColumns: `${COLUMN_WIDTH_PX}px 1fr`,
        }}
      >
        <RailTree
          userWorkspaces={userWorkspaces}
          workspaceIdNum={workspaceIdNum}
          armedDeleteWorkspaceId={armedDeleteWorkspaceId}
          renamingWorkspaceId={renamingWorkspaceId}
          renameDraft={renameDraft}
          orgName={
            orgs.find((o) => o.org_id === workspace?.org_id)?.name ??
            orgs[0]?.name ??
            null
          }
          newWorkspace={{
            open: newWorkspaceOpen,
            name: newWorkspaceName,
            busy: newWorkspaceBusy,
            setOpen: setNewWorkspaceOpen,
            setName: setNewWorkspaceName,
          }}
          activeRepoConnections={repoConnections}
          onNavigate={navigate}
          onDeleteWorkspace={(w) => void handleDeleteWorkspace(w)}
          onStartRenameWorkspace={startRenameWorkspace}
          onRenameDraftChange={setRenameDraft}
          onSubmitRenameWorkspace={(e) => void submitRenameWorkspace(e)}
          onCancelRenameWorkspace={cancelRenameWorkspace}
          onSubmitCreateWorkspace={submitCreateWorkspace}
          onClearError={() => setError("")}
          appVersion={appVersion}
          envLabel={envLabel}
        />

        <section className="relative min-h-0">
          <TabBar
            orgId={workspace?.org_id ?? null}
            workspaceId={workspaceIdNum}
            entitlements={entitlements}
          />
          {/* ActiveWatchersStrip removed 2026-04-27 — chrome was
              competing with the sandbox view for vertical space and
              the watcher state surfaces in the drawer + Personas tab
              already. The Operations tab is the within-Workspace
              cockpit; cross-Workspace orchestration lives at the
              top-level /orchestrator route. */}
          <div className="absolute inset-0 flex">
            <div className="relative min-h-0 flex-1">
              <ErrorToast error={error} onClear={() => setError("")} />

              {activeTab === "sandbox" && (
                <SandboxTab
                  workspaceId={workspaceIdNum}
                  entitlements={entitlements}
                />
              )}

              {activeTab !== "chat" && activeTab !== "sandbox" && (
                <>
                  {activeTab === "operator" && (
                    <OperatorTab
                      workspace={workspace}
                      agents={agents}
                      presenceByAgentId={presenceByAgentId}
                      activeInflight={activeInflight}
                      activeRoutingCount={activeRoutingCount}
                      pendingCommitCount={pendingCommitIdByMessageId.size}
                      pendingTurnCount={
                        pendingTurnRows.filter(
                          (row) =>
                            row.status === "pending" || row.status === "locked",
                        ).length
                      }
                      recentMessages={messages.slice(-5).reverse()}
                      workspacePaused={workspacePaused}
                      entitlements={entitlements}
                    />
                  )}
                  {activeTab === "road" && (
                    <RoadTab workspaceId={workspaceIdNum} />
                  )}
                  {activeTab === "files" && (
                    <FilesTab workspaceId={workspaceIdNum} />
                  )}
                  {activeTab === "telemetry" && (
                    <TelemetryTab workspaceId={workspaceIdNum} />
                  )}
                  {activeTab === "personas" && (
                    <PersonasTab
                      workspaceId={workspaceIdNum}
                      orgId={workspace?.org_id ?? null}
                    />
                  )}
                  {activeTab === "recipes" && (
                    <RecipesTab
                      workspaceId={workspaceIdNum}
                      orgId={workspace?.org_id ?? null}
                    />
                  )}
                  {activeTab === "receipts" && (
                    <ReceiptsTab workspaceId={workspaceIdNum} />
                  )}
                  {activeTab === "newsfeed" && (
                    <NewsfeedTab workspaceId={workspaceIdNum} />
                  )}
                  {activeTab === "models" && (
                    <ModelsTab
                      workspaceId={workspaceIdNum}
                      orgId={workspace?.org_id ?? null}
                      onOpenDrawerContent={openDrawerContent}
                    />
                  )}
                  {activeTab === "usage" && (
                    <UsageTab
                      orgId={workspace?.org_id ?? null}
                      workspaceId={workspaceIdNum}
                    />
                  )}
                  {activeTab === "settings" && (
                    <SettingsTab
                      orgId={workspace?.org_id ?? null}
                      workspaceId={workspaceIdNum}
                      onOpenDrawerContent={openDrawerContent}
                    />
                  )}
                  {activeTab === "status" && (
                    <StatusTab workspaceId={workspaceIdNum} />
                  )}
                </>
              )}

              {/* A34 — chat moved to the drawer (renderTeamChat below).
                * Chat removed from TabBar's TABS list, so
                * activeTab === "chat" is structurally unreachable in
                * the canon flow. The block stays as a no-op fossil
                * and will be deleted in a follow-up cleanup. */}
              {activeTab === "chat" && (
                <>
                  <div
                    ref={messagesScrollRef}
                    onScroll={handleMessagesScroll}
                    className="absolute left-0 right-0 top-14 overflow-y-auto px-4 pt-3"
                    style={{ bottom: composerHeight }}
                    data-testid="messages-list"
                  >
                    {messages.length === 0 && workspaceIdNum > 0 && (
                      <p className="text-sm text-muted">
                        No messages yet. Post the first one.
                      </p>
                    )}
                    <ul className="flex flex-col">
                      {messages.map((message, idx) => {
                        const agent = agents.find(
                          (a) => a.agent_id === message.author_agent_id,
                        );
                        const attachments =
                          attachmentsByMessageId.get(message.message_id) ?? [];
                        const isStreamingComplete =
                          completedStreamingMessageIds.has(message.message_id);
                        const streamingBody =
                          streamingBodyByMessageId.get(message.message_id);
                        const body =
                          message.author_kind === "agent" &&
                          !isStreamingComplete &&
                          streamingBody !== undefined
                            ? streamingBody
                            : message.body;

                        if (
                          message.author_kind === "agent" &&
                          body.trim() === "" &&
                          attachments.length === 0
                        ) {
                          return null;
                        }

                        const prev = messages[idx - 1];
                        const sameAuthor =
                          prev !== undefined &&
                          prev.author_kind === message.author_kind &&
                          prev.author_agent_id === message.author_agent_id &&
                          message.created_at - prev.created_at <
                            5 * 60 * 1_000_000;

                        const authorName =
                          message.author_kind === "agent"
                            ? agent?.display_name ?? "Agent"
                            : message.author_kind === "user"
                              ? user?.display_name ?? user?.email ?? "You"
                              : message.author_kind;
                        const authorColor =
                          message.author_kind === "agent"
                            ? "text-accent"
                            : message.author_kind === "user"
                              ? "text-primary"
                              : "text-warning";

                        const timestamp = new Date(
                          message.created_at / 1000,
                        ).toLocaleTimeString(undefined, {
                          hour: "2-digit",
                          minute: "2-digit",
                        });

                        const messageArmed =
                          armedDeleteMessageId === message.message_id;
                        return (
                          <MessageCard
                            key={message.message_id}
                            author={{
                              name: authorName,
                              colorClass: authorColor,
                              meta: agent ? `@${agent.seat_key}` : undefined,
                            }}
                            alignment={
                              message.author_kind === "user" ? "me" : "other"
                            }
                            sameAuthor={sameAuthor}
                            timestamp={timestamp}
                            onDelete={() =>
                              void handleDeleteMessage(message.message_id)
                            }
                            deleteArmed={messageArmed}
                            testId={`message-${message.author_kind}`}
                          >
                            <div>
                              {body.trim() === "" ? null : (
                                <div className="prose prose-sm max-w-none wt-prose">
                                  <ReactMarkdown
                                    remarkPlugins={[remarkGfm]}
                                    rehypePlugins={[rehypeHighlight]}
                                  >
                                    {body}
                                  </ReactMarkdown>
                                </div>
                              )}
                              {(() => {
                                const costBadge = attachments.find(
                                  (a) => a.kind === "run_summary",
                                );
                                const others = attachments.filter(
                                  (a) => a.kind !== "run_summary",
                                );
                                return (
                                  <>
                                    {others.length > 0 && (
                                      <div
                                        className="mt-2 flex flex-col gap-2"
                                        data-testid={`message-${message.message_id}-attachments`}
                                      >
                                        {others.map((attachment) => (
                                          <AttachmentRenderer
                                            key={attachment.attachment_id}
                                            attachment={attachment}
                                            workspaceId={workspaceIdNum}
                                            pendingCommitIdByMessageId={
                                              pendingCommitIdByMessageId
                                            }
                                          />
                                        ))}
                                      </div>
                                    )}
                                    {costBadge && (
                                      <CostBadge
                                        attachment={costBadge}
                                        messageId={message.message_id}
                                      />
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </MessageCard>
                        );
                      })}
                    </ul>
                  </div>
                  {showScrollToBottom && (
                    <button
                      type="button"
                      onClick={scrollToBottom}
                      style={{ bottom: composerHeight + 12 }}
                      className="glass-blur absolute right-6 z-30 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border border-edge bg-surface-raised/80 text-muted shadow-[var(--wt-shadow)] transition-colors hover:border-accent hover:text-primary"
                      aria-label="Scroll to bottom"
                      title="Scroll to bottom"
                      data-testid="scroll-to-bottom"
                    >
                      <ArrowDown size={14} />
                    </button>
                  )}

                  {workspaceIdNum > 0 && workspacePaused && (
                    <WorkspaceUpgradeGate workspaceId={workspaceIdNum} />
                  )}
                  {workspaceIdNum > 0 && !workspacePaused && (
                    <Composer
                      workspaceId={workspaceIdNum}
                      composerRef={composerRef}
                      messageInputRef={messageInputRef}
                      mentionPopupRef={mentionPopupRef}
                      mentionListRef={mentionListRef}
                      newMessage={newMessage}
                      onNewMessageChange={setNewMessage}
                      onMentionCaretChange={setMentionCaret}
                      onKeyDown={handleComposerKeyDown}
                      onSubmit={submitMessage}
                      busy={busy}
                      agents={agents}
                      activeRoutingCount={activeRoutingCount}
                      activeInflight={activeInflight}
                      directReplyInFlight={directReplyInFlight}
                      targetedAgents={targetedAgents}
                      mentionMatches={mentionMatches}
                      mentionActiveIdx={mentionActiveIdx}
                      onMentionActiveIdxChange={setMentionActiveIdx}
                      workspaceAgentCount={workspaceAgentIds.size}
                      modelSort={modelSort}
                      onModelSortChange={setModelSort}
                      onMentionPick={applyMention}
                    />
                  )}
                </>
              )}
            </div>
            <div className="flex pt-14">
              <DrawerHandle
                open={drawerOpen}
                onToggle={() => setDrawerOpen((v) => !v)}
                title={drawerTitle()}
                testId="room-drawer-handle"
              />
              <Drawer open={drawerOpen} testId="room-drawer">
                {renderDrawerBody()}
              </Drawer>
            </div>
          </div>
        </section>
      </main>

      <datalist id="openrouter-models">
        {openRouterModels.map((model) => (
          <option key={model.id} value={model.id}>
            {model.name}
          </option>
        ))}
      </datalist>
    </div>
  );
}
